import { useState, useEffect, useCallback } from 'react';
import { useEffectiveBusinessId } from '@/hooks';
import { fetchPendingTrips, assignTripToProject, dismissTrip } from '../services';
import type { Trip } from '../types';

interface UsePendingTripsReturn {
  trips: Trip[];
  isLoading: boolean;
  error: string | null;
  assignToProject: (tripId: string, projectId: string) => Promise<void>;
  dismissTrip: (tripId: string) => Promise<void>;
  refetch: () => Promise<void>;
}

export function usePendingTrips(): UsePendingTripsReturn {
  const effectiveBusinessId = useEffectiveBusinessId();
  const [trips, setTrips] = useState<Trip[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!effectiveBusinessId) {
      setTrips([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const data = await fetchPendingTrips(effectiveBusinessId);
      setTrips(data);
    } catch (err) {
      console.error('Error fetching pending trips:', err);
      setError(err instanceof Error ? err.message : 'Failed to load trips');
    } finally {
      setIsLoading(false);
    }
  }, [effectiveBusinessId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAssignToProject = useCallback(
    async (tripId: string, projectId: string) => {
      await assignTripToProject(tripId, projectId);
      // Remove from local state
      setTrips((prev) => prev.filter((t) => t.id !== tripId));
    },
    []
  );

  const handleDismissTrip = useCallback(async (tripId: string) => {
    await dismissTrip(tripId);
    // Remove from local state
    setTrips((prev) => prev.filter((t) => t.id !== tripId));
  }, []);

  return {
    trips,
    isLoading,
    error,
    assignToProject: handleAssignToProject,
    dismissTrip: handleDismissTrip,
    refetch: fetchData,
  };
}